import React, { useState, useEffect, useMemo } from "react";
import cogoToast from "cogo-toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Spinner,
} from "reactstrap";
import { genericGet, genericPost } from "../../../services/Api";
import { SUCCESS_RESULT, ENDPOINTS } from "../../../Constants";
import {
  FaEdit,
  FaExclamationCircle,
  FaTimesCircle,
  FaCheckCircle,
  FaEye,
  FaSearch,
} from "react-icons/fa";
import { useHistory } from "react-router-dom";
import { Chart } from "react-google-charts";
import "react-overlay-loader/styles.css";
import { Loader } from "react-overlay-loader";

const PENDING_APPROVAL = 10000;
const APPROVED_STATUS = 10001;
const CANCELLED_STATUS = 10002;
const REJECTED_APPROVAL = 10002;

const getLeaveOverallStatusCode = (row) => {
  const raw = row?.status ?? row?.leave_status ?? row?.request_status;
  if (raw !== null && raw !== undefined && raw !== "") {
    return Number(raw);
  }
  const statusName = String(row?.status_name || row?.status_label || "").toLowerCase();
  if (statusName.includes("cancel")) return CANCELLED_STATUS;
  if (statusName.includes("approv")) return APPROVED_STATUS;
  if (statusName.includes("pending")) return PENDING_APPROVAL;
  return PENDING_APPROVAL;
};

const MyLeaveRequests = () => {
  const history = useHistory();
  const [is_loading, setIsLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [tableSearch, setTableSearch] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    fetch_data();
    return () => {};
  }, []);

  const isLeaveRequestCancelled = (row) => getLeaveOverallStatusCode(row) === CANCELLED_STATUS;

  const isFullyApproved = (row) =>
    Number(row?.supervisor_approval) === APPROVED_STATUS &&
    Number(row?.hr_approval) === APPROVED_STATUS;

  const canCancelLeaveRequest = (row) => {
    if (!row || isLeaveRequestCancelled(row) || isFullyApproved(row)) return false;
    return true;
  };

  const canEditLeaveRequest = (row) => {
    if (!row || isLeaveRequestCancelled(row) || isFullyApproved(row)) return false;
    const supervisorApproval = Number(row.supervisor_approval);
    const hrApproval = Number(row.hr_approval);
    const bothPending =
      supervisorApproval === PENDING_APPROVAL && hrApproval === PENDING_APPROVAL;
    const hasRejection =
      supervisorApproval === REJECTED_APPROVAL || hrApproval === REJECTED_APPROVAL;
    const onlySupervisorApproved =
      supervisorApproval === APPROVED_STATUS && hrApproval !== APPROVED_STATUS;
    return bothPending || hasRejection || onlySupervisorApproved;
  };

  const fetch_data = () => {
    setIsLoading(true);
    genericGet(ENDPOINTS.MY_LEAVE_REQUESTS)
      .then((res) => {
        setIsLoading(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          const responseData = res.data.data || {};
          setChartData(responseData.summary || []);
          setRowData(Array.isArray(responseData.details) ? responseData.details : []);
        } else {
          cogoToast.error(res?.data?.message || "Failed to load leave requests.");
        }
      })
      .catch(() => {
        setIsLoading(false);
        cogoToast.error("Failed to load leave requests.");
      });
  };

  const cancel_leave = (id) => {
    setIsCancelling(true);
    genericPost(ENDPOINTS.CANCEL_LEAVE_REQUEST, { request_id: id })
      .then((res) => {
        setIsCancelling(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          cogoToast.info(res.data.message, {});
          setShowCancelModal(false);
          setCancelTarget(null);
          fetch_data();
        } else {
          cogoToast.error(res?.data?.message || "Failed to cancel leave request.");
        }
      })
      .catch(() => {
        setIsCancelling(false);
        cogoToast.error("Failed to cancel leave request.");
      });
  };

  const openCancelModal = (row) => {
    if (!canCancelLeaveRequest(row)) return;
    setCancelTarget(row);
    setShowCancelModal(true);
  };

  const closeCancelModal = () => {
    if (isCancelling) return;
    setShowCancelModal(false);
    setCancelTarget(null);
  };

  const viewLeave = (item) => {
    history.push({ pathname: "/hrm/leave/print", state: { data: item } });
  };

  const renderOverallStatus = (row) => {
    if (getLeaveOverallStatusCode(row) === CANCELLED_STATUS) {
      return (
        <span className="badge bg-secondary">
          <FaTimesCircle className="me-1" /> Cancelled
        </span>
      );
    }
    const supervisorApproved = Number(row?.supervisor_approval) === APPROVED_STATUS;
    const hrApproved = Number(row?.hr_approval) === APPROVED_STATUS;
    if (supervisorApproved && hrApproved) {
      return (
        <span className="badge bg-success">
          <FaCheckCircle className="me-1" /> Approved
        </span>
      );
    }
    return null;
  };

  const renderApprovalLine = (label, approval, approvalDate) => {
    const value = Number(approval);
    if (value === PENDING_APPROVAL) {
      return (
        <div className="d-flex align-items-center gap-1 text-muted">
          <FaExclamationCircle /> <span>{label}:</span> <strong>Pending</strong>
        </div>
      );
    }
    if (value === REJECTED_APPROVAL) {
      return (
        <div className="d-flex align-items-center gap-1 text-danger">
          <FaTimesCircle /> <span>{label}:</span> <strong>Rejected</strong>
        </div>
      );
    }
    return (
      <div className="d-flex align-items-center gap-1 text-success">
        <FaCheckCircle /> <span>{label}:</span>{" "}
        <strong>Approved{approvalDate ? ` · ${approvalDate}` : ""}</strong>
      </div>
    );
  };

  const filteredData = useMemo(() => {
    if (!tableSearch.trim()) return rowData;
    const q = tableSearch.trim().toLowerCase();
    return rowData.filter((item) => {
      const supervisorApproved = Number(item?.supervisor_approval) === APPROVED_STATUS;
      const hrApproved = Number(item?.hr_approval) === APPROVED_STATUS;
      const statusLabel =
        getLeaveOverallStatusCode(item) === CANCELLED_STATUS
          ? "cancelled"
          : supervisorApproved && hrApproved
            ? "approved"
            : "";
      const haystack = [
        item?.leave_type?.name,
        item?.request_date,
        item?.total_days,
        item?.leave_reason,
        item?.created_by?.name,
        item?.supervisor_approval_date,
        item?.hr_approval_date,
        item?.status_name,
        statusLabel,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rowData, tableSearch]);

  const chartOptions = {
    chart: {
      title: "ANNUAL LEAVES SUMMARIES",
      subtitle: "Various annual leaves and remaining days",
    },
    hAxis: { title: "Leave Types" },
    vAxis: { title: "Days" },
    seriesType: "bars",
  };

  return (
    <div className="default-card-style">
      <Loader loading={is_loading && !showCancelModal} />

      <div className="mb-4">
        <h2 className="text-center mb-1" style={{ fontWeight: 700 }}>
          ANNUAL LEAVE TYPE SUMMARIES
        </h2>
        <p className="text-center text-muted mb-3">Various annual leaves and remaining days</p>
        <Chart
          chartType="ComboChart"
          width="100%"
          height="400px"
          data={chartData}
          options={chartOptions}
        />
      </div>

      <h2 style={{ fontWeight: 700, marginBottom: "1rem" }}>MY LEAVE REQUESTS</h2>

      <div className="mb-3">
        <div className="input-group" style={{ maxWidth: "480px" }}>
          <span className="input-group-text bg-primary text-white">
            <FaSearch />
          </span>
          <Input
            type="text"
            placeholder="Search leave type, date, reason..."
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
          />
        </div>
        <small className="text-muted d-block mt-2">
          {filteredData.length} request{filteredData.length === 1 ? "" : "s"}
          {tableSearch ? ` matching "${tableSearch}"` : ""}
        </small>
      </div>

      <div className="table-responsive shadow-sm rounded border">
        <table className="table table-hover mb-0 align-middle" style={{ fontSize: "0.9rem" }}>
          <thead className="table-dark">
            <tr>
              <th style={{ width: "44px" }}>#</th>
              <th style={{ minWidth: "140px" }}>Leave</th>
              <th style={{ minWidth: "160px" }}>Details</th>
              <th style={{ minWidth: "100px" }}>Status</th>
              <th style={{ minWidth: "200px" }}>Approvals</th>
              <th style={{ width: "130px", whiteSpace: "nowrap" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {is_loading && rowData.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-4 text-muted">
                  <Spinner size="sm" className="me-2" /> Loading requests...
                </td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-4 text-muted">
                  {tableSearch ? "No requests match your search." : "No leave requests found."}
                </td>
              </tr>
            ) : (
              filteredData.map((item, index) => (
                <tr
                  key={item.id || index}
                  onClick={() => viewLeave(item)}
                  style={{ cursor: "pointer" }}
                  title="Click to view leave details"
                >
                  <td className="text-muted fw-semibold">{index + 1}</td>
                  <td>
                    <div className="fw-semibold">{item?.leave_type?.name || "—"}</div>
                    <small className="text-muted d-block">
                      {item?.request_date || "—"} · <strong>{item?.total_days ?? "—"}</strong> day
                      {Number(item?.total_days) === 1 ? "" : "s"}
                    </small>
                  </td>
                  <td>
                    <div
                      className="text-truncate"
                      style={{ maxWidth: "280px" }}
                      title={item?.leave_reason || ""}
                    >
                      {item?.leave_reason || "—"}
                    </div>
                    <small className="text-muted">
                      By: {item?.created_by?.name || "—"}
                    </small>
                  </td>
                  <td>{renderOverallStatus(item)}</td>
                  <td style={{ fontSize: "0.82rem", lineHeight: 1.5 }}>
                    {renderApprovalLine("Supervisor", item.supervisor_approval, item.supervisor_approval_date)}
                    {renderApprovalLine("HR", item.hr_approval, item.hr_approval_date)}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex flex-column gap-1">
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={() => viewLeave(item)}
                      >
                        <FaEye /> View
                      </button>
                      {canEditLeaveRequest(item) && (
                        <button
                          type="button"
                          className="btn btn-success btn-sm"
                          onClick={() =>
                            history.push({
                              pathname: "/hrm/leave/create",
                              state: { request_id: item.id, editLeave: item },
                            })
                          }
                        >
                          <FaEdit /> Edit
                        </button>
                      )}
                      {canCancelLeaveRequest(item) && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => openCancelModal(item)}
                        >
                          <FaTimesCircle /> Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={showCancelModal} toggle={closeCancelModal} centered>
        <ModalHeader toggle={closeCancelModal}>Cancel leave request</ModalHeader>
        <ModalBody>
          {cancelTarget ? (
            <>
              <p className="mb-3">
                Are you sure you want to cancel this leave request? This action cannot be undone.
              </p>
              <div
                className="p-3 rounded border bg-light"
                style={{ fontSize: "0.95rem", lineHeight: 1.6 }}
              >
                <div>
                  <strong>Leave type:</strong> {cancelTarget?.leave_type?.name || "—"}
                </div>
                <div>
                  <strong>Request date:</strong> {cancelTarget?.request_date || "—"}
                </div>
                <div>
                  <strong>Total days:</strong> {cancelTarget?.total_days ?? "—"}
                </div>
                <div>
                  <strong>Reason:</strong> {cancelTarget?.leave_reason || "—"}
                </div>
                <div className="mt-2">
                  <strong>Overall status:</strong> {renderOverallStatus(cancelTarget)}
                </div>
                <div className="mt-2 pt-2 border-top">
                  {renderApprovalLine(
                    "Supervisor",
                    cancelTarget.supervisor_approval,
                    cancelTarget.supervisor_approval_date
                  )}
                  {renderApprovalLine(
                    "HR",
                    cancelTarget.hr_approval,
                    cancelTarget.hr_approval_date
                  )}
                </div>
              </div>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeCancelModal} disabled={isCancelling}>
            Keep request
          </Button>
          <Button
            color="danger"
            onClick={() => cancelTarget?.id && cancel_leave(cancelTarget.id)}
            disabled={isCancelling || !cancelTarget?.id}
          >
            {isCancelling ? (
              <>
                <Spinner size="sm" className="me-2" />
                Cancelling...
              </>
            ) : (
              "Yes, cancel leave"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default MyLeaveRequests;
